import React, { useEffect, useState } from "react";
import Dashboard from "../../layouts/Dashboard";
import { ReactLoading } from "../Dashboard/TinManagement/import";
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CheckCircleFilled,
  ExclamationCircleOutlined,
  ExportOutlined,
  InfoCircleFilled,
  InfoCircleOutlined,
  SearchOutlined,
  SyncOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  Avatar,
  Button,
  Card,
  Modal,
  Select,
  Table,
  Tag,
  Tooltip,
  message,
} from "antd";
import { useHistory, useParams } from "react-router-dom";
import useAxios from "../../hooks/useAxios";
import { getAllUsers } from "../../services/users/users.service";

const { Option } = Select;

const TransferLeads = () => {
  const { id } = useParams();
  const history = useHistory();
  const api = useAxios();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [rule, setRule] = useState(null);
  const [ruleName, setRuleName] = useState("");
  const [existingRecruiters, setExistingRecruiters] = useState([]);
  const [allRecruiters, setAllRecruiters] = useState([]);
  const [selectedMappings, setSelectedMappings] = useState({});
  const [recruiterLeadCounts, setRecruiterLeadCounts] = useState({});
  const [totalRuleLeads, setTotalRuleLeads] = useState(0);

  // Candidate inspection modal state
  const [candidateModalVisible, setCandidateModalVisible] = useState(false);
  const [candidatesList, setCandidatesList] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);

  // Fetch full Rule details and recruiter lead counts in parallel
  const getRuleDetailsAndLeadCounts = async () => {
    try {
      const [{ data: ruleData }, { data: countData }] = await Promise.all([
        api.get(`/ruleEngine/${id}`, { tokenType: "user" }),
        api.get(`/ruleEngine/recruiterLeadsCount/${id}`, { tokenType: "user" }),
      ]);

      setRule(ruleData);
      setRuleName(ruleData?.ruleName || countData?.ruleName || "Rule");
      setTotalRuleLeads(countData?.totalRuleLeads || 0);

      // assignedRecruiters only contains recruiters with leadCount > 0 for transfer display
      const recruitersWithLeads = countData?.assignedRecruiters || [];
      const formattedRecruiters = recruitersWithLeads.map((r) => ({
        _id: r?.recruiterId,
        id: r?.recruiterId,
        name: r?.name,
        email: r?.email,
        leadCount: r?.leadCount || 0,
      }));

      setExistingRecruiters(formattedRecruiters);

      // Initialize mapping and lead count state for each recruiter with leads
      const initialMap = {};
      const countsMap = {};
      formattedRecruiters.forEach((rec) => {
        const recId = rec._id;
        initialMap[recId] = [];
        countsMap[recId] = rec.leadCount;
      });

      setSelectedMappings(initialMap);
      setRecruiterLeadCounts(countsMap);

      return formattedRecruiters;
    } catch (err) {
      console.error("Error fetching rule details and lead counts:", err);
      message.error("Failed to load rule configuration.");
      return [];
    }
  };

  // Fetch all recruiters available from recruiterCampaignUsers API
  const getAllRecruiterUsers = async () => {
    try {
      const { data } = await api.get("/user/recruiterCampaignUsers", {
        tokenType: "user",
      });
      let usersList = Array.isArray(data)
        ? data
        : data?.data || data?.users || [];
      setAllRecruiters(usersList);
    } catch (err) {
      console.error("Error fetching recruiter campaign users:", err);
    }
  };

  // Fetch candidate list for inspect modal
  const fetchRuleCandidates = async () => {
    try {
      setLoadingCandidates(true);
      setCandidateModalVisible(true);
      const { data } = await api.get(
        `/ruleEngine/unassigned-leads?PageNo=1&PageSize=50`,
        { tokenType: "user" }
      );
      setCandidatesList(data?.candidateList || []);
    } catch (err) {
      console.error("Failed to load candidates:", err);
    } finally {
      setLoadingCandidates(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        await Promise.all([
          getRuleDetailsAndLeadCounts(),
          getAllRecruiterUsers(),
        ]);
      } catch (err) {
        console.error("Initialization error:", err);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      init();
    }
  }, [id]);

  // Handle destination recruiter multi-select change
  const handleDestinationChange = (existingRecruiterId, selectedRecruiterIds) => {
    setSelectedMappings((prev) => ({
      ...prev,
      [existingRecruiterId]: selectedRecruiterIds,
    }));
  };

  // Recruiters who have leads > 0
  const recruitersWithLeads = existingRecruiters.filter((rec) => {
    const recId = rec?._id || rec?.id || rec;
    const count =
      recruiterLeadCounts[recId] !== undefined
        ? recruiterLeadCounts[recId]
        : rec?.leadCount || 0;
    return Number(count) > 0;
  });

  // Count confirmed mappings (only for recruiters with leads)
  const confirmedMappingsCount = Object.entries(selectedMappings).filter(
    ([recId, val]) => {
      const count =
        recruiterLeadCounts[recId] !== undefined
          ? recruiterLeadCounts[recId]
          : 0;
      return Number(count) > 0 && Array.isArray(val) && val.length > 0;
    }
  ).length;

  // Handle Transfer Leads Submission
  const handleDone = async () => {
    try {
      const activeTransferMappings = Object.entries(selectedMappings)
        .filter(([_, toIds]) => Array.isArray(toIds) && toIds.length > 0)
        .map(([fromRecruiterId, toRecruiterIds]) => ({
          fromRecruiterId,
          toRecruiterIds,
        }));

      if (activeTransferMappings.length === 0) {
        message.warning("Please select at least one destination recruiter before confirming.");
        return;
      }

      setSubmitting(true);

      // Collect all destination recruiter IDs
      const destinationIds = activeTransferMappings.flatMap((m) => m.toRecruiterIds || []);

      // Preserve ALL recruiters originally assigned to the rule (including those with 0 leads)
      const originalRuleRecruiterIds = (rule?.recruiter || [])
        .map((r) => (typeof r === "object" ? r?._id || r?.id : r))
        .filter(Boolean)
        .map((id) => String(id));

      const destinationIdStrings = destinationIds
        .filter(Boolean)
        .map((id) => String(id));

      // Combine all original recruiters plus newly assigned destination recruiters
      const updatedRecruiterIds = [
        ...new Set([...originalRuleRecruiterIds, ...destinationIdStrings]),
      ];

      const payload = {
        ruleName: rule?.ruleName || ruleName || "Rule",
        priority: rule?.priority !== undefined ? rule.priority : 1,
        description: rule?.description || "Updated rule with new recruiter mapping",
        isActive: rule?.isActive !== undefined ? rule.isActive : true,
        groups: rule?.groups || [],
        recruiter: updatedRecruiterIds.length > 0 ? updatedRecruiterIds : destinationIdStrings,
        transferMapping: activeTransferMappings,
      };

      await api.put(`/ruleEngine/transferLeads/${id}`, payload, {
        tokenType: "user",
      });

      message.success("Leads transferred successfully!");
      history.push("/rule-engine");
    } catch (err) {
      console.error("Transfer error:", err);
      const errMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Failed to transfer leads. Please try again.";
      message.error(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const candidateColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Mobile No",
      dataIndex: "mobileNo",
      key: "mobileNo",
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (val) => (val ? new Date(val).toLocaleDateString("en-GB") : "-"),
    },
  ];

  return (
    <Dashboard title="Rule Engine - Manage Rules & Transfer Leads">
      {loading && (
        <ReactLoading type="spin" color="#0000FF" className="spinnerBg" />
      )}

      <div className="w-full max-w-7xl mx-auto pb-10">
        {/* Navigation Breadcrumb & Inspect Action */}
        <div className="flex justify-between items-center mb-4 pt-1">
          <button
            onClick={() => history.push("/rule-engine")}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-md transition-all shadow-sm outline-none cursor-pointer"
          >
            <ArrowLeftOutlined style={{ fontSize: "13px" }} />
            <span>Back to Manage Rules</span>
          </button>

          {/* <button
            onClick={fetchRuleCandidates}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-md transition-all shadow-sm outline-none cursor-pointer"
          >
            <span>Inspect Active Leads</span>
            <Tag className="!m-0 !bg-gray-100 !text-gray-700 !border-gray-300 !font-semibold">
              {totalRuleLeads}
            </Tag>
          </button> */}
        </div>

        {/* Title Header Card */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-5">
          <div className="flex items-center gap-3">
            <div
              className="flex items-center justify-center rounded-full"
              style={{
                width: "48px",
                height: "48px",
                backgroundColor: "#FFF7E6",
                border: "1px solid #FFE7BA",
              }}
            >
              <SyncOutlined
                style={{
                  fontSize: "22px",
                  color: "#FA8C16",
                  fontWeight: "bold",
                }}
              />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-0">
                Transfer Leads
              </h2>
              <p className="text-sm font-medium text-gray-500 mb-0">
                Rule: <span className="text-gray-800 font-semibold">{ruleName || "Null Rule"}</span>
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg shadow-sm">
              <span className="text-xs text-gray-500 uppercase font-medium">
                Total Rule Leads:
              </span>
              <span className="text-base font-bold text-gray-900">
                {totalRuleLeads}
              </span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg shadow-sm">
              <span className="text-xs text-gray-500 uppercase font-medium">
                Assigned Recruiters:
              </span>
              <span className="text-base font-bold text-gray-900">
                {existingRecruiters?.length || 0}
              </span>
            </div>
          </div>
        </div>

        {/* Primary Information Alert Banner */}
        {/* <div
          className="mb-4 p-4 rounded-lg flex items-start gap-3 border"
          style={{
            backgroundColor: "#FFFBE6",
            borderColor: "#FFE58F",
          }}
        >
          <div className="mt-0.5">
            <InfoCircleOutlined style={{ color: "#FAAD14", fontSize: "18px" }} />
          </div>
          <div className="text-xs leading-relaxed text-amber-900">
            <span className="font-bold">Note: Disclamer</span>
            For new/upcoming leads, please transfer, delete, or edit this rule. This action will transfer only the existing leads currently in the system. Transferring leads will reassign all historical candidates currently assigned to the selected recruiters without altering future lead routing rules. Multiple destination recruiters can be selected for even distribution.
          </div>
        </div> */}

        {/* Secondary Transfer Behavior Notice */}
        <div
          className="mb-6 px-4 py-3 rounded-lg flex items-center justify-between border"
          style={{
            backgroundColor: "#FFFFFF",
            borderColor: "#FFD591",
          }}
        >
          <div className="flex items-center gap-2.5 text-xs text-gray-700">
            <InfoCircleFilled style={{ color: "#FA8C16", fontSize: "15px" }} />
            <span>
              <span className="font-semibold text-gray-900">Disclamer: </span>
              Only existing leads will be transferred. To change the assignment for new/upcoming leads, edit, delete, or update the rule.
            </span>
          </div>

          {/* <button
            onClick={fetchRuleCandidates}
            className="text-xs font-semibold text-orange-600 hover:text-orange-700 flex items-center gap-1.5 outline-none bg-transparent border-0 cursor-pointer p-0"
          >
            <ExportOutlined />
            <span>View Candidate List</span>
          </button> */}
        </div>

        {/* Existing Recruiters Transfer Configuration Card */}
        <Card
          className="shadow-sm border-gray-200 rounded-xl"
          bodyStyle={{ padding: "24px" }}
        >
          <div className="flex flex-col md:flex-row md:items-center justify-between pb-4 border-b border-gray-100 mb-6">
            <div>
              <h3 className="text-lg font-bold text-gray-800 mb-0.5">
                Existing Recruiters Transfer Configuration
              </h3>
              <p className="text-xs text-gray-500 mb-0">
                Select one or multiple destination recruiters to transfer existing leads. Leads will be distributed evenly.
              </p>
            </div>
            <div className="mt-2 md:mt-0">
              <span className="text-xs font-medium text-gray-600">
                Status:{" "}
                <span className={`font-bold ${confirmedMappingsCount > 0 ? "text-green-600" : "text-gray-900"}`}>
                  {confirmedMappingsCount} of {recruitersWithLeads.length || 0}
                </span>{" "}
                Mappings Confirmed
              </span>
            </div>
          </div>

          {/* Table Header */}
          <div className="grid grid-cols-12 gap-4 pb-3 border-b border-gray-200 text-[11px] font-bold tracking-wider text-gray-400 uppercase">
            <div className="col-span-12 md:col-span-5">Existing Recruiter</div>
            <div className="col-span-12 md:col-span-2 text-center hidden md:block">
              Transfer Mapping
            </div>
            <div className="col-span-12 md:col-span-5">
              Transfer Existing Leads To (Multi-Select)
            </div>
          </div>

          {/* Table Body / Rows */}
          {existingRecruiters.length === 0 ? (
            <div className="py-12 text-center text-gray-400">
              <UserOutlined className="text-3xl mb-2 text-gray-300" />
              <p className="text-sm">No recruiters currently assigned to this rule.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {existingRecruiters.map((recruiter, index) => {
                const recId = recruiter?._id || recruiter?.id || recruiter;
                const recName = recruiter?.name || `Recruiter ${index + 1}`;
                const recEmail =
                  recruiter?.email ||
                  `${recName?.toLowerCase().replace(/\s+/g, "")}@company.com`;
                const leadCount = recruiterLeadCounts[recId] || 0;
                const selectedDests = selectedMappings[recId] || [];

                return (
                  <div
                    key={recId || index}
                    className="grid grid-cols-12 gap-4 py-5 items-center hover:bg-gray-50/60 transition-colors rounded-lg px-2"
                  >
                    {/* Existing Recruiter Info */}
                    <div className="col-span-12 md:col-span-5 flex items-center gap-3">
                      <Avatar
                        size={42}
                        className="bg-gray-300 text-gray-700 font-bold text-base flex-shrink-0"
                      >
                        {recName?.charAt(0)?.toUpperCase()}
                      </Avatar>
                      <div className="min-w-0">
                        <div className="font-bold text-sm text-gray-900 truncate">
                          {recName}
                        </div>
                        <div className="text-xs text-gray-500 truncate flex items-center gap-1.5 mt-0.5">
                          <span>{recEmail}</span>
                          <span>•</span>
                          <Tag className="!m-0 !text-[11px] !px-1.5 !py-0 !bg-gray-100 !text-gray-600 !border-gray-200 rounded">
                            {leadCount} leads
                          </Tag>
                        </div>
                      </div>
                    </div>

                    {/* Arrow / Mapping Indicator */}
                    <div className="col-span-12 md:col-span-2 flex justify-center items-center my-1 md:my-0">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center border shadow-2xs ${
                          leadCount > 0
                            ? "bg-gray-100 text-gray-500 border-gray-200"
                            : "bg-gray-50 text-gray-300 border-gray-100"
                        }`}
                      >
                        <ArrowRightOutlined className="text-xs" />
                      </div>
                    </div>

                    {/* Destination Recruiters Dropdown */}
                    <div className="col-span-12 md:col-span-5">
                      {leadCount > 0 ? (
                        <Select
                          mode="multiple"
                          allowClear
                          showSearch
                          size="large"
                          className="w-full"
                          placeholder="Select destination recruiter(s)..."
                          value={selectedDests}
                          onChange={(vals) => handleDestinationChange(recId, vals)}
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            option?.children
                              ? String(option.children).toLowerCase().includes(input.toLowerCase())
                              : false
                          }
                        >
                          {allRecruiters
                            .filter((u) => String(u?._id || u?.id) !== String(recId))
                            .sort((a, b) =>
                              (a?.name || "")
                                .trim()
                                .toLowerCase()
                                .localeCompare((b?.name || "").trim().toLowerCase())
                            )
                            .map((u) => {
                              const uId = String(u?._id || u?.id);
                              const uName = u?.name || "Unknown";
                              const uEmail = u?.email ? ` (${u.email})` : "";
                              return (
                                <Option key={uId} value={uId}>
                                  {uName}
                                  {uEmail}
                                </Option>
                              );
                            })}
                        </Select>
                      ) : (
                        <div className="flex items-center text-xs text-gray-400 italic py-2">
                          No leads to transfer
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Footer Action Bar */}
          <div className="flex flex-col sm:flex-row items-center justify-between pt-6 mt-6 border-t border-gray-100 gap-4">
            <p className="text-xs text-gray-400 italic mb-0 text-center sm:text-left">
              Please select and confirm at least one destination recruiter before clicking Done.
            </p>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => history.push("/rule-engine")}
                className="btn btn-secondary px-6 font-medium cursor-pointer"
                style={{
                  borderRadius: "8px",
                  padding: "8px 20px",
                  border: "1px solid #d9d9d9",
                  backgroundColor: "#fff",
                  color: "#595959",
                  fontSize: "14px",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={confirmedMappingsCount === 0 || submitting}
                onClick={handleDone}
                className="btn btn-primary flex gap-[10px] items-center"
                style={{
                  opacity: confirmedMappingsCount === 0 || submitting ? 0.65 : 1,
                  cursor: confirmedMappingsCount === 0 || submitting ? "not-allowed" : "pointer",
                }}
              >
                <SyncOutlined
                  spin={submitting}
                  style={{ fontSize: "1.1rem", fontWeight: 600 }}
                />
                <span className="flex items-center text-[16px] font-semibold">
                  {submitting ? "Transferring..." : "Transfer Leads"}
                </span>
              </button>
            </div>
          </div>
        </Card>
      </div>

      {/* Candidate List Modal for Inspecting Leads */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <UserOutlined className="text-blue-500" />
            <span className="font-bold text-base">Active Candidates for Rule: {ruleName}</span>
          </div>
        }
        open={candidateModalVisible}
        onCancel={() => setCandidateModalVisible(false)}
        width={750}
        footer={[
          <Button key="close" onClick={() => setCandidateModalVisible(false)}>
            Close
          </Button>,
        ]}
      >
        <Table
          columns={candidateColumns}
          dataSource={candidatesList}
          rowKey={(r) => r?._id || r?.mobileNo}
          loading={loadingCandidates}
          pagination={{ pageSize: 5 }}
          size="small"
        />
      </Modal>
    </Dashboard>
  );
};

export default TransferLeads;
